import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStore } from '@tanstack/react-form';
import { useMemo } from 'react';
import { z } from 'zod';

import { useIntegrationCRMProviders } from '@/api/integration';
import { type InstanceDetailDataType, useCRMPipelineReportMetadata } from '@/api/organization';
import { LinkButton } from '@/components/buttons';
import {
  NumberInput,
  NumberInputGroup,
  NumberInputInput,
  NumberInputTrigger,
  useTypedAppFormContext,
} from '@/components/Form';
import { FieldError, FieldLabel } from '@/components/Form/fields';
import {
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/Form/pickers/primitives/select';
import SelectEmpty from '@/components/Form/pickers/primitives/select/SelectEmpty';
import SelectStatus from '@/components/Form/pickers/primitives/select/SelectStatus';
import InfoPopover from '@/components/InfoPopover';
import { Spinner } from '@/components/loaders';
import { ReportName } from '@/components/ReportName';
import { Results } from '@/components/Results';
import { SupportLink, Text } from '@/components/typography';
import { isHubspotReportUrlValid, isSalesforceReportUrlValid } from '@/helper/validator';
import { ROUTES } from '@/router';

type Props = {
  instance: InstanceDetailDataType;
  isEditMode: boolean;
};

const DEFAULT_PIPELINE_INFLUENCE_WINDOW = 90;

const CompanyOpportunitySettings = ({ instance, isEditMode }: Props) => {
  const form = useTypedAppFormContext({ defaultValues: {} as InstanceDetailDataType });
  const {
    hasHubspotConnection,
    hasSalesforceConnection,
    isLoading: isCRMProvidersLoading,
    error: crmProvidersError,
  } = useIntegrationCRMProviders();
  const hasActiveCRMConnection = hasSalesforceConnection || hasHubspotConnection;
  const canEdit = isEditMode && hasActiveCRMConnection;
  const crmReportURL = useStore(form.store, (state) => state.values.pipelineReport?.url ?? '');
  const hasRetainedHubspotReportUrl =
    crmReportURL.trim() !== '' && isHubspotReportUrlValid(crmReportURL);
  const isSalesforce =
    hasSalesforceConnection || (!hasHubspotConnection && !hasRetainedHubspotReportUrl);
  const { reportMetadata, isLoading, error } = useCRMPipelineReportMetadata(
    !isCRMProvidersLoading && hasActiveCRMConnection ? crmReportURL : '',
  );
  const reportErrorMessage = error?.getFirstError?.().message;
  const pipelineReportAmountValue = useStore(
    form.store,
    (state) => state.values.pipelineReport?.fieldName ?? '',
  );
  const opportunityAmountFieldOptions = useMemo(
    () => reportMetadata?.currencyFieldNames ?? [],
    [reportMetadata?.currencyFieldNames],
  );
  const effectivePipelineReportAmountValue = useMemo(() => {
    if (!crmReportURL.trim() || opportunityAmountFieldOptions.length === 0 || !!error) {
      return '';
    }

    return opportunityAmountFieldOptions.includes(pipelineReportAmountValue)
      ? pipelineReportAmountValue
      : (opportunityAmountFieldOptions[0] ?? '');
  }, [crmReportURL, error, opportunityAmountFieldOptions, pipelineReportAmountValue]);

  const hasRetainedPipelineReport = Boolean(
    crmReportURL.trim() || pipelineReportAmountValue.trim(),
  );
  const reportUrlFieldSchema = z
    .string()
    .refine(
      (value) =>
        isSalesforce ? isSalesforceReportUrlValid(value) : isHubspotReportUrlValid(value),
      {
        message: isSalesforce
          ? t`The Salesforce link is incorrect. Valid URLs should resemble the following pattern: 'https://<your-subdomain>.lightning.force.com/lightning/r/Report/<report-id>/view'.`
          : t`The HubSpot link is incorrect. Valid URLs should resemble the following pattern: 'https://app.hubspot.com/contacts/<contact-id>/objectLists/<list-id>'.`,
      },
    )
    .refine((value) => !reportErrorMessage || value.trim() !== crmReportURL.trim(), {
      message: reportErrorMessage ?? '',
    });

  if (isCRMProvidersLoading) {
    return <Spinner variant="spoke" />;
  }

  if (crmProvidersError) {
    return (
      <Results subtitle={<Trans>Error loading CRM provider data, please try again later.</Trans>} />
    );
  }

  if (!hasActiveCRMConnection && !hasRetainedPipelineReport) {
    return (
      <Results
        subtitle={
          <div className="flex flex-col items-center">
            <span>
              <Trans>A CRM connection is required for Pipeline and ROI reporting.</Trans>
            </span>
            <span>
              <Trans>Please connect your CRM to activate this feature.</Trans>
            </span>
          </div>
        }
        extra={
          <LinkButton variant="primary" to={ROUTES.integrationByCategory('crm')}>
            <Trans>Connect CRM</Trans>
          </LinkButton>
        }
      />
    );
  }

  return (
    <>
      {hasActiveCRMConnection ? (
        <Text variant="body1">
          <Text variant="body1" weight="bold">
            <Trans>Note:</Trans>
          </Text>{' '}
          {isSalesforce ? (
            <Trans>
              The Opportunity report must contain the Opportunity ID field and an Amount field.
            </Trans>
          ) : (
            <Trans>Create a Deal Segment List in HubSpot to use for pipeline reporting.</Trans>
          )}
        </Text>
      ) : (
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <Text variant="body1">
            <Trans>
              Your CRM is disconnected. Saved pipeline report settings are shown as read-only.
            </Trans>
          </Text>
          <LinkButton variant="primary" to={ROUTES.integrationByCategory('crm')}>
            <Trans>Connect CRM</Trans>
          </LinkButton>
        </div>
      )}
      <div className="grid grid-cols-1 gap-x-10 gap-y-6 lg:grid-cols-2 xl:grid-cols-3">
        <form.AppField
          name="pipelineReport.url"
          validators={{
            onChange: reportUrlFieldSchema,
            onDynamic: reportUrlFieldSchema,
          }}
        >
          {(field) => (
            <field.Field className="xl:col-span-2">
              <div className="flex items-center gap-2">
                <FieldLabel htmlFor="company-settings-opportunity-url">
                  {isSalesforce ? (
                    <Trans>Pipeline Report Link</Trans>
                  ) : (
                    <Trans>Deal Segment Link</Trans>
                  )}
                </FieldLabel>
                <InfoPopover
                  title={
                    isSalesforce ? t`Salesforce Pipeline Report Link` : t`HubSpot Deal Segment Link`
                  }
                  body={
                    isSalesforce ? (
                      <Trans>
                        Custom pipeline reporting is available to define which opportunities and
                        amounts the Channel99 platform uses. This report only requires Opportunity
                        ID and Amount fields.{' '}
                        <SupportLink
                          variant="body2"
                          color="static-green"
                          path="SALESFORCE_OPPORTUNITY_REPORTING"
                        >
                          More information here
                        </SupportLink>
                      </Trans>
                    ) : (
                      <Trans>
                        Custom pipeline reporting is available to define which opportunities and
                        amounts the Channel99 platform uses.{' '}
                        <SupportLink
                          variant="body2"
                          color="static-green"
                          path="HUBSPOT_OPPORTUNITY_REPORTING"
                        >
                          More information here
                        </SupportLink>
                      </Trans>
                    )
                  }
                />
              </div>
              {canEdit ? (
                <field.TextInput
                  id="company-settings-opportunity-url"
                  placeholder={
                    isSalesforce
                      ? t`Enter Salesforce Report Link`
                      : t`Enter HubSpot Deal Segment Link`
                  }
                  size="medium"
                />
              ) : field.state.value ? (
                <Text className="wrap-break-word" variant="body1">
                  {field.state.value}
                </Text>
              ) : (
                <Text className="wrap-break-word" variant="body1" italic weight="extra-light">
                  <Trans>no custom opportunity report</Trans>
                </Text>
              )}
              <ReportName reportName={reportMetadata?.name} isLoading={isLoading} />
              <field.Error />
              {reportErrorMessage && (!field.state.meta.isTouched || field.state.meta.isValid) && (
                <FieldError errors={[{ message: reportErrorMessage }]} />
              )}
            </field.Field>
          )}
        </form.AppField>
        <form.AppField
          name="pipelineReport.influenceWindow"
          defaultValue={
            instance.pipelineReport?.influenceWindow ?? DEFAULT_PIPELINE_INFLUENCE_WINDOW
          }
        >
          {(field) => {
            const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

            return (
              <field.Field>
                <div className="flex items-center gap-2">
                  <FieldLabel htmlFor="company-settings-opportunity-influence-window">
                    <Trans>Opportunity Influence Window</Trans>
                  </FieldLabel>
                  <InfoPopover
                    title={t`Opportunity Influence Window`}
                    body={
                      <Trans>
                        Customizing the influence window will increase or reduce the number of days
                        a visit can influence an opportunity in regards to the opportunity create
                        date.{' '}
                        <SupportLink
                          variant="body2"
                          color="static-green"
                          path="OPPORTUNITY_INFLUENCE_WINDOW"
                        >
                          Learn more
                        </SupportLink>
                      </Trans>
                    }
                  />
                </div>
                <div className="flex w-full items-center gap-1">
                  {canEdit ? (
                    <NumberInput
                      id="company-settings-opportunity-influence-window"
                      size="medium"
                      style={{ width: 90 }}
                      value={field.state.value ?? null}
                      onValueChange={(value) => field.handleChange(value ?? undefined)}
                      max={365}
                      min={1}
                    >
                      <NumberInputGroup>
                        <NumberInputInput
                          aria-invalid={isInvalid || undefined}
                          onBlur={field.handleBlur}
                        />
                        <NumberInputTrigger />
                      </NumberInputGroup>
                    </NumberInput>
                  ) : (
                    <Text variant="body1">
                      {field.state.value ?? DEFAULT_PIPELINE_INFLUENCE_WINDOW}
                    </Text>
                  )}
                  <Text variant="body1">
                    <Trans>Days</Trans>
                  </Text>
                </div>
                <field.Error />
              </field.Field>
            );
          }}
        </form.AppField>
        <form.AppField name="pipelineReport.fieldName">
          {(field) => {
            const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

            return (
              <field.Field className="max-w-90 lg:col-start-1">
                <div className="flex items-center gap-2">
                  <FieldLabel htmlFor="company-settings-opportunity-field-name">
                    <Trans>Opportunity Amount Field</Trans>
                  </FieldLabel>
                  <InfoPopover
                    title={t`Opportunity Amount Field`}
                    body={
                      <Trans>
                        This field is used with a custom opportunity report to identify which amount
                        field should be used in platform reporting, Choose your preferred amount
                        field to be used from the provided report.
                      </Trans>
                    }
                  />
                </div>
                {canEdit ? (
                  <field.Select<string>
                    size="medium"
                    disabled={
                      !crmReportURL.trim() || opportunityAmountFieldOptions.length === 0 || !!error
                    }
                    isLoading={isLoading}
                    value={effectivePipelineReportAmountValue || null}
                    onValueChange={field.handleChange}
                  >
                    <field.SelectTrigger
                      id="company-settings-opportunity-field-name"
                      className="w-64"
                      aria-invalid={isInvalid || undefined}
                      onBlur={field.handleBlur}
                    >
                      <SelectValue placeholder={t`Select Field`} />
                    </field.SelectTrigger>
                    <SelectContent>
                      <SelectStatus />
                      <SelectEmpty items={opportunityAmountFieldOptions} />
                      {opportunityAmountFieldOptions.map((fieldName) => (
                        <SelectItem key={fieldName} value={fieldName}>
                          {fieldName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </field.Select>
                ) : (
                  <Text className="wrap-break-word" variant="body1">
                    {effectivePipelineReportAmountValue || field.state.value}
                  </Text>
                )}
                <field.Error />
              </field.Field>
            );
          }}
        </form.AppField>
      </div>
    </>
  );
};

export default CompanyOpportunitySettings;
